import QuickBooksSourceBanner from './QuickBooksSourceBanner';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import quickbooksService, {
  formatCurrency,
  formatCurrencyPrecise,
  formatCompactCurrency,
  formatDate,
  getEntityColor,
} from '../../services/quickbooksService';

export default function IntercompanyView() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entities, setEntities] = useState([]);
  const [selectedFromEntity, setSelectedFromEntity] = useState('all');
  const [selectedToEntity, setSelectedToEntity] = useState('all');
  const [intercompanyBalances, setIntercompanyBalances] = useState([]);
  const [intercompanyMismatches, setIntercompanyMismatches] = useState([]);
  const [intercompanySummary, setIntercompanySummary] = useState(null);
  const [intercompanyTransactions, setIntercompanyTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, [selectedFromEntity, selectedToEntity]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (selectedFromEntity !== 'all') params.from_connector_id = selectedFromEntity;
      if (selectedToEntity !== 'all') params.to_entity = selectedToEntity;

      const [entitiesRes, balancesRes, transactionsRes] = await Promise.all([
        quickbooksService.getQuickBooksEntities(),
        quickbooksService.getIntercompanyBalances(),
        quickbooksService.getIntercompanyTransactions(params),
      ]);

      if (entitiesRes.success) setEntities(entitiesRes.data);
      if (balancesRes.success) {
        setIntercompanyBalances(balancesRes.data.accounts || balancesRes.data || []);
        setIntercompanyMismatches(balancesRes.data.mismatches || []);
        setIntercompanySummary(balancesRes.data.summary || null);
      }
      if (transactionsRes.success) setIntercompanyTransactions(transactionsRes.data || []);

    } catch (err) {
      console.error('Error loading intercompany data:', err);
      setError('Failed to load intercompany data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFromEntityChange = (event) => {
    setSelectedFromEntity(event.target.value);
  };

  const handleToEntityChange = (event) => {
    setSelectedToEntity(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const filteredTransactions = intercompanyTransactions.filter(tx =>
    tx.memo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.from_entity?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.to_entity?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const totalNetBalance = intercompanyBalances.reduce((sum, ic) => sum + (ic.balance || ic.net_balance || 0), 0);
  const totalTransactions = intercompanyTransactions.reduce((sum, tx) => sum + (Math.abs(tx.amount) || 0), 0);

  return (
    <Box sx={{ p: 3 }}>
      <QuickBooksSourceBanner />
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={600}>
            Intercompany Transactions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track payments and transfers between entities
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>From Entity</InputLabel>
            <Select
              value={selectedFromEntity}
              label="From Entity"
              onChange={handleFromEntityChange}
            >
              <MenuItem value="all">All Entities</MenuItem>
              {entities.map((entity) => (
                <MenuItem key={entity.connector_id} value={entity.connector_id}>
                  {entity.company_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>To Entity</InputLabel>
            <Select
              value={selectedToEntity}
              label="To Entity"
              onChange={handleToEntityChange}
            >
              <MenuItem value="all">All Entities</MenuItem>
              {entities.map((entity) => (
                <MenuItem key={entity.connector_id} value={entity.company_name}>
                  {entity.company_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh Data">
            <IconButton onClick={loadData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Intercompany Accounts
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {intercompanyBalances.length}
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: 'primary.light',
                  borderRadius: '50%',
                  p: 1.5,
                }}>
                  <CompareArrowsIcon sx={{ color: 'primary.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Net Intercompany Position
                  </Typography>
                  <Typography
                    variant="h4"
                    component="div"
                    sx={{
                      fontWeight: 600,
                      color: totalNetBalance >= 0 ? 'success.main' : 'error.main'
                    }}
                  >
                    {formatCompactCurrency(totalNetBalance)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderColor: intercompanyMismatches.length > 0 ? 'error.main' : 'transparent', borderWidth: intercompanyMismatches.length > 0 ? 2 : 0, borderStyle: 'solid' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Unmatched Entries
                  </Typography>
                  <Typography
                    variant="h4"
                    component="div"
                    sx={{ fontWeight: 600, color: intercompanyMismatches.length > 0 ? 'error.main' : 'success.main' }}
                  >
                    {intercompanyMismatches.length}
                  </Typography>
                  {intercompanyMismatches.length > 0 && (
                    <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                      {formatCompactCurrency(intercompanySummary?.total_mismatch_value || 0)} unreconciled
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Transaction Volume
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: 'info.main' }}>
                    {formatCompactCurrency(totalTransactions)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {intercompanyTransactions.length} transactions
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Mismatch Alert */}
      {intercompanyMismatches.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Intercompany Mismatches Detected:</strong> {intercompanyMismatches.length} intercompany balance{intercompanyMismatches.length !== 1 ? 's' : ''} do not have matching counterparty entries.
            Total mismatch value: {formatCompactCurrency(intercompanySummary?.total_mismatch_value || 0)}.
            These should be investigated and reconciled.
          </Typography>
        </Alert>
      )}

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Intercompany Reconciliation:</strong> For <strong>Internal</strong> relationships (both entities in our system),
          if Entity A owes Entity B, then: Entity A should have a <strong>Payable</strong> and Entity B should have a matching <strong>Receivable</strong> of the same amount.
          <strong> External</strong> relationships (third parties like Supernova) are regular payables/receivables and don't require matching entries.
        </Typography>
      </Alert>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Account Balances" />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Mismatches
                {intercompanyMismatches.length > 0 && (
                  <Chip label={intercompanyMismatches.length} size="small" color="error" />
                )}
              </Box>
            }
          />
          <Tab label="Transaction History" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Intercompany Account Balances
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Intercompany accounts and balances across entities. <strong>Internal</strong> = both entities in our system (requires matching entries). <strong>External</strong> = third-party (no matching entry expected).
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Entity</TableCell>
                    <TableCell>Account Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Related Party</TableCell>
                    <TableCell>Relationship</TableCell>
                    <TableCell align="right">Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {intercompanyBalances.map((ic, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Chip
                          label={ic.entity_name || '-'}
                          size="small"
                          sx={{
                            backgroundColor: getEntityColor(ic.entity_name),
                            color: 'white',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={500}>
                          {ic.account_name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ic.is_asset ? 'Receivable' : 'Payable'}
                          size="small"
                          color={ic.is_asset ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {ic.related_entity ? (
                          <Chip
                            label={ic.related_entity}
                            size="small"
                            sx={{
                              backgroundColor: ic.related_connector_id ? getEntityColor(ic.related_entity) : '#9e9e9e',
                              color: 'white',
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ic.related_connector_id ? 'Internal' : 'External'}
                          size="small"
                          color={ic.related_connector_id ? 'primary' : 'default'}
                          variant={ic.related_connector_id ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          fontWeight={600}
                          color={(ic.balance || 0) >= 0 ? 'success.main' : 'error.main'}
                        >
                          {formatCurrencyPrecise(ic.balance || 0)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {intercompanyBalances.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No intercompany accounts found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Intercompany Mismatches
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These entries do not have matching counterparty records. For proper intercompany accounting,
              both sides of a transaction should be recorded.
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Entity Recording</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Related Entity</TableCell>
                    <TableCell>Expected Entry</TableCell>
                    <TableCell align="right">Recorded Amount</TableCell>
                    <TableCell align="right">Counterparty Amount</TableCell>
                    <TableCell align="right">Difference</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {intercompanyMismatches.map((mismatch, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Chip
                          label={mismatch.from_entity}
                          size="small"
                          sx={{
                            backgroundColor: getEntityColor(mismatch.from_entity),
                            color: 'white',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={500}>
                          {mismatch.from_account || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={mismatch.from_type}
                          size="small"
                          color={mismatch.from_type === 'Receivable' ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={mismatch.to_entity}
                          size="small"
                          sx={{
                            backgroundColor: getEntityColor(mismatch.to_entity),
                            color: 'white',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={mismatch.type === 'missing_counterparty' ? `Missing ${mismatch.expected_counterparty_type}` : 'Amount Mismatch'}
                          size="small"
                          color="error"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={600}>
                          {formatCurrencyPrecise(mismatch.from_balance)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={600} color={mismatch.to_balance === 0 ? 'error.main' : 'text.primary'}>
                          {mismatch.to_balance === 0 ? 'NOT FOUND' : formatCurrencyPrecise(mismatch.to_balance)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={600} color="error.main">
                          {formatCurrencyPrecise(mismatch.difference)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {intercompanyMismatches.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Box sx={{ py: 3 }}>
                          <Typography variant="body1" color="success.main" fontWeight={600}>
                            All Balanced!
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            No intercompany mismatches found - all entries have matching counterparties.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Transaction History
              </Typography>
              <TextField
                size="small"
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 300 }}
              />
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>From Entity</TableCell>
                    <TableCell></TableCell>
                    <TableCell>To Entity</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTransactions.map((tx, index) => (
                    <TableRow key={index}>
                      <TableCell>{formatDate(tx.txn_date)}</TableCell>
                      <TableCell>
                        <Chip
                          label={tx.from_entity_short || tx.from_entity}
                          size="small"
                          sx={{
                            backgroundColor: getEntityColor(tx.from_entity),
                            color: 'white',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <ArrowForwardIcon fontSize="small" color="action" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={tx.to_entity_short || tx.to_entity}
                          size="small"
                          sx={{
                            backgroundColor: getEntityColor(tx.to_entity),
                            color: 'white',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={tx.txn_type || 'Transfer'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={tx.memo || '-'}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 250,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {tx.memo || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          fontWeight={600}
                          color={tx.amount >= 0 ? 'success.main' : 'error.main'}
                        >
                          {formatCurrencyPrecise(Math.abs(tx.amount))}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredTransactions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No intercompany transactions found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {filteredTransactions.length > 100 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                Showing 100 of {filteredTransactions.length} transactions
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
